import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, apiRequest, isOfflineApiError } from './api/client'
import type { AuthenticatedUser } from './api/types'
import { navigate, unauthorizedRedirectTarget, usePathname } from './app/navigation'
import { cacheVerifiedSession, readVerifiedOfflineSession } from './app/offlineSession'
import { AppShell } from './components/AppShell'
import { ErrorState, PageLoader } from './components/States'
import { DashboardPage } from './pages/DashboardPage'
import { HistoryPage } from './pages/HistoryPage'
import { LibraryPage } from './pages/LibraryPage'
import { LoginPage } from './pages/LoginPage'
import { PlanPage } from './pages/PlanPage'
import { SettingsPage } from './pages/SettingsPage'
import { WorkoutPage } from './pages/WorkoutPage'
import './App.css'

function CurrentPage({ pathname }: { pathname: string }) {
  if (pathname === '/') return <DashboardPage />
  if (pathname === '/plan') return <PlanPage />
  if (pathname === '/library') return <LibraryPage />
  if (pathname === '/history') return <HistoryPage />
  if (pathname === '/settings') return <SettingsPage />
  const workout = pathname.match(/^\/workout\/(\d{4}-\d{2}-\d{2})$/)
  if (workout) return <WorkoutPage date={workout[1]} />
  return (
    <div className="state-panel state-panel--empty">
      <strong>Stránka nebyla nalezena</strong>
      <button type="button" className="button button--primary" onClick={() => navigate('/')}>Zpět na dnešek</button>
    </div>
  )
}

export default function App() {
  const pathname = usePathname()
  const session = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const cached = readVerifiedOfflineSession()
      if (!navigator.onLine && cached) return cached
      try {
        const user = await apiRequest<AuthenticatedUser>('/auth/me')
        cacheVerifiedSession(user)
        return user
      } catch (error) {
        if ((!(error instanceof ApiError) || isOfflineApiError(error)) && cached) return cached
        throw error
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    const revalidate = () => { void session.refetch() }
    window.addEventListener('online', revalidate)
    return () => window.removeEventListener('online', revalidate)
  }, [session])

  useEffect(() => {
    const unauthorized = () => {
      const target = unauthorizedRedirectTarget(window.location.pathname)
      if (target) window.location.replace(target)
    }
    window.addEventListener('repinu:unauthorized', unauthorized)
    return () => window.removeEventListener('repinu:unauthorized', unauthorized)
  }, [])

  useEffect(() => {
    if (session.data && pathname === '/login') navigate('/', true)
  }, [pathname, session.data])

  if (session.isPending) return <PageLoader label="Ověřuji přihlášení…" />
  if (session.isError) {
    if (session.error instanceof ApiError && session.error.status === 401) return <LoginPage />
    return <div className="boot-state"><ErrorState error={session.error} onRetry={() => session.refetch()} /></div>
  }
  if (pathname === '/login') return null

  return <AppShell user={session.data}><CurrentPage pathname={pathname} /></AppShell>
}